import React, { useEffect, useState } from 'react';
import { Feature, getLocalizedProperty, ruleUsesLevel } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LevelAttributes } from '@loyalty/shared-common/gamification-api-client';
import { QlfConfirmationDialog } from '@qualifio/ui-commons';
import {
  QlfFilterDetails,
  QlfMasterDetail,
  QlfSimpleDetail,
} from '@qualifio/ui-commons/dist/components/qlf-master-detail';
import { RuleType } from '@loyalty/shared-common/rule-api-client';
import { Skeleton } from '@mui/material';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { LevelDetails } from '../../libs/levels/components/detail/LevelDetails';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { useConfirmDialog } from '../../libs/shared/use-confirm-dialog';
import { useFeatures } from '../../libs/features/FeaturesContext';
import { useLevels } from '../../libs/levels/LevelsContext';
import { useLevelsConfig } from '../../libs/levels/LevelsConfigContext';
import { useRules } from '../../libs/rules/RulesContext';

const useStyles = makeStyles(() => ({
  deletedRule: {
    fontWeight: 600,
  },
  deletedRules: {
    marginTop: '20px',
  },
}));

export function LevelsIndexPage() {
  const { t, i18n } = useTranslation();
  const { levelsConfig, getLevelConfig, isLoading } = useLevelsConfig();
  const {
    levels,
    selectedLevel,
    setSelectedLevel,
    createLevel,
    deleteLevel,
    deleteUnsavedLevel,
    deleteUnsavedLevels,
  } = useLevels();
  const [searchField, setSearchField] = useState('');
  const { burningRules, earningRules, fetchRules } = useRules();
  const classes = useStyles();
  const { features } = useFeatures();
  const { remove: levelRemove, write: levelWrite } = features[Feature.LEVEL];
  const { askConfirmation, props, setProps, ...dialogProps } = useConfirmDialog();

  useEffect(() => {
    if (!earningRules) {
      fetchRules(RuleType.EARN);
    }
    if (!burningRules) {
      fetchRules(RuleType.BURN);
    }
    return () => {
      deleteUnsavedLevels();
    };
  }, []);

  const templateHeader = () => (
    <QlfFilterDetails
      placeholder={t('levels.sidebar.search')}
      onAdd={createLevel}
      disableAdd={!levelWrite || !levelsConfig}
      onAddTooltip={levelWrite ? '' : t('shared.action.permission.required')}
      onChange={e => setSearchField(e.target.value)}
    />
  );

  const templateRow = (props: { data: LevelAttributes }) => {
    const { data: level } = props;
    const options = level.createdAt
      ? [
          {
            node: <p>{t('generic.actions.hard-delete')}</p>,
            startIcon: <FontAwesomeIcon fixedWidth={true} icon={faTrash} />,
            callback: () => {
              setProps({ levelId: level.id });
              askConfirmation(() => {
                deleteLevel(level.id).then(() => {
                  fetchRules(RuleType.EARN);
                  fetchRules(RuleType.BURN);
                });
              });
            },
            disabled: !levelRemove,
            tooltip: levelRemove ? '' : t('shared.action.permission.required'),
          },
        ]
      : [
          {
            node: <p>{t('generic.actions.hard-delete')}</p>,
            startIcon: <FontAwesomeIcon fixedWidth={true} icon={faTrash} />,
            callback: () => deleteUnsavedLevel(level.id),
            disabled: !levelRemove,
            tooltip: levelRemove ? '' : t('shared.action.permission.required'),
          },
        ];

    return (
      <QlfSimpleDetail
        state={level.id === selectedLevel?.id ? 'active' : 'default'}
        options={options}
        callback={() => setSelectedLevel(props.data)}
        content={getLocalizedProperty(level, 'name', i18n.language)}
        image={getLocalizedProperty(level, 'image', i18n.language)}
      />
    );
  };

  const items = levels
    .filter(
      level =>
        !searchField || getLocalizedProperty(level, 'name', i18n.language)?.includes(searchField),
    )
    .sort((a, b) => a.minCurrencyAmount - b.minCurrencyAmount);

  if (isLoading(getLevelConfig)) {
    return <Skeleton />;
  }

  return (
    <>
      <QlfMasterDetailContainer>
        <QlfMasterDetail
          templateHeader={templateHeader}
          templateRow={templateRow}
          templateDetail={LevelDetails}
          items={items}
        />
      </QlfMasterDetailContainer>
      <QlfConfirmationDialog
        {...dialogProps}
        title={t('levels.confirm-delete.title')}
        //content={t('levels.confirm-delete.message')}
        content={
          (
            <>
              {t('levels.confirm-delete.message')}
              <div className={classes.deletedRules}>
                {[...earningRules, ...burningRules].filter(rule =>
                  ruleUsesLevel(rule.criteria, props?.levelId),
                ).length ? (
                  <div>
                    {t('levels.confirm-delete.rules-warning')}
                    {[...earningRules, ...burningRules]
                      .filter(rule => ruleUsesLevel(rule.criteria, props?.levelId))
                      .map(rule => (
                        <div key={rule.id} className={classes.deletedRule}>
                          {`- ${rule.name}`}
                        </div>
                      ))}
                  </div>
                ) : (
                  ''
                )}
              </div>
            </>
          ) as any
        }
        color={'warning'}
        confirmButtonText={t('generic.actions.hard-delete')}
        cancelButtonText={t('generic.actions.cancel')}
      />
    </>
  );
}
